import { DateTime } from "luxon";

/**
 * Format a CTA date range for display.
 *
 * Returns:
 *  - "May 8 – May 22, 2026" when both dates are present and in different months
 *  - "May 8 – 22, 2026" when both dates are in the same month and year
 *  - "Due May 22, 2026" when only the end date is present
 *  - "Starts May 8, 2026" when only the start date is present
 *  - "" when neither date is present
 *
 * @param {string|null} startDate - ISO date string (yyyy-MM-dd)
 * @param {string|null} endDate - ISO date string (yyyy-MM-dd)
 * @returns {string}
 */
export function formatCtaDateRange(startDate, endDate) {
    const start = startDate ? DateTime.fromFormat(startDate, "yyyy-MM-dd") : null;
    const end = endDate ? DateTime.fromFormat(endDate, "yyyy-MM-dd") : null;

    if (start?.isValid && end?.isValid) {
        if (start.year === end.year && start.month === end.month) {
            return `${start.toFormat("MMM d")} – ${end.toFormat("d, yyyy")}`;
        }
        if (start.year === end.year) {
            return `${start.toFormat("MMM d")} – ${end.toFormat("MMM d, yyyy")}`;
        }
        return `${start.toFormat("MMM d, yyyy")} – ${end.toFormat("MMM d, yyyy")}`;
    }

    if (end?.isValid) {
        return `Due ${end.toFormat("MMM d, yyyy")}`;
    }

    if (start?.isValid) {
        return `Starts ${start.toFormat("MMM d, yyyy")}`;
    }

    return "";
}

/**
 * Whether a section's start date is still in the future (i.e. it hasn't opened yet).
 * A start date of today counts as already started.
 *
 * @param {string|null|undefined} startDate - ISO date string (yyyy-MM-dd)
 * @returns {boolean}
 */
export function isStartDateInFuture(startDate) {
    if (!startDate) {
        return false;
    }
    const start = DateTime.fromFormat(startDate, "yyyy-MM-dd");
    return start.isValid && start.startOf("day") > DateTime.now().startOf("day");
}

/**
 * Format the single date label shown on a performance review section header.
 *
 * - "Starts May 8, 2026" when the start date is still in the future (section not open yet)
 * - "Due May 22, 2026" when the section has already started (start date today or in the past)
 * - "" when neither a future start nor a due date is available
 *
 * @param {string|null|undefined} startDate - ISO date string (yyyy-MM-dd)
 * @param {string|null|undefined} dueDate - ISO date string (yyyy-MM-dd)
 * @returns {string}
 */
export function formatSectionDateLabel(startDate, dueDate) {
    const start = startDate ? DateTime.fromFormat(startDate, "yyyy-MM-dd") : null;
    const due = dueDate ? DateTime.fromFormat(dueDate, "yyyy-MM-dd") : null;

    if (isStartDateInFuture(startDate)) {
        return `Starts ${start.toFormat("MMM d, yyyy")}`;
    }

    if (due?.isValid) {
        return `Due ${due.toFormat("MMM d, yyyy")}`;
    }

    return "";
}
